import React, { useState, useRef, useEffect } from 'react';
import { validateImageFile } from '../../services/api';
import ImagePreview from './ImagePreview';
import { ArrowUp, Stop, Plus, Image, Microphone } from '@phosphor-icons/react';

const MAX_MESSAGE_LENGTH = 5000;
const CHAR_COUNT_THRESHOLD = 4000;

const MessageInput = ({ onSendMessage, disabled, isStreaming, onStopStreaming, gradeLevel, onVoiceToggle }) => {
  const [message, setMessage] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [error, setError] = useState(null);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isElementary = gradeLevel === 'elementary';
  const MAX_IMAGES = 2;

  // Adjust textarea height when content changes
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Call adjustTextareaHeight when message changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowImageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle image selection
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    setError(null);

    // Check total count
    if (selectedImages.length + files.length > MAX_IMAGES) {
      setError(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    // Validate each file
    const validFiles = [];
    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      validFiles.push(file);
    }

    setSelectedImages([...selectedImages, ...validFiles]);
    setShowImageMenu(false);

    // Reset file input
    event.target.value = '';
  };

  // Handle image removal
  const handleRemoveImage = (index) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
    setError(null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedMessage = message.trim();
    const imagesToSend = [...selectedImages];

    // Validate: need message OR images
    if (!trimmedMessage && imagesToSend.length === 0) {
      return;
    }

    if (isSubmitting || disabled) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // Clear inputs immediately (optimistic) so user sees it right away
    setMessage('');
    setSelectedImages([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      await onSendMessage(trimmedMessage, imagesToSend);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.message || 'Failed to send message');
      // Restore message on failure so user doesn't lose their input
      setMessage(trimmedMessage);
      setSelectedImages(imagesToSend);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle keyboard shortcuts (Enter to send, Shift+Enter for new line)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const charsRemaining = MAX_MESSAGE_LENGTH - message.length;
  const showCharCount = message.length >= CHAR_COUNT_THRESHOLD;
  const canSend = (message.trim() || selectedImages.length > 0) && !disabled && !isSubmitting;

  return (
    <div id="chat-input" className="p-2 sm:p-4 border-t border-gray-200">
      {/* Error display */}
      {error && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Image preview */}
      {selectedImages.length > 0 && (
        <ImagePreview
          images={selectedImages}
          onRemove={handleRemoveImage}
        />
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        {/* Add button container */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            className={`${isElementary ? 'w-10 h-10 sm:w-12 sm:h-12' : 'w-8 h-8 sm:w-10 sm:h-10'} rounded-full flex items-center justify-center transition-all ${
              disabled || selectedImages.length >= MAX_IMAGES
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setShowImageMenu(!showImageMenu)}
            disabled={disabled || selectedImages.length >= MAX_IMAGES}
            aria-label="Add content"
          >
            <Plus size={isElementary ? 22 : 18} weight="bold" />
          </button>

          {/* Dropdown menu */}
          {showImageMenu && (
            <div className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px] z-10">
              <button
                type="button"
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                onClick={() => fileInputRef.current?.click()}
              >
                <span className="flex items-center gap-2">
                  <Image size={16} className="text-gray-500" />
                  Add Images
                </span>
                <span className="text-xs text-gray-500">
                  ({selectedImages.length}/{MAX_IMAGES})
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Voice toggle button */}
        {onVoiceToggle && (
          <button
            type="button"
            className={`${isElementary ? 'w-10 h-10 sm:w-12 sm:h-12' : 'w-8 h-8 sm:w-10 sm:h-10'} rounded-full flex items-center justify-center transition-all ${
              disabled
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-50 text-indigo-500 hover:bg-indigo-100'
            }`}
            onClick={onVoiceToggle}
            disabled={disabled}
            aria-label="Talk to Brillabee"
            title="Talk to Brillabee"
          >
            <Microphone size={isElementary ? 22 : 18} weight="regular" />
          </button>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />

        {/* Message input container */}
        <div className="input-container relative flex-1 bg-gray-100 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-indigo-200 transition-all">
          <textarea
            id="message-input"
            ref={textareaRef}
            className={`w-full ${isElementary ? 'py-3 px-3 sm:px-4 pr-12 sm:pr-14 text-base min-h-[60px]' : 'py-2 sm:py-3 px-2 sm:px-4 pr-10 sm:pr-12 text-sm sm:text-base'} bg-gray-100 border-none resize-none max-h-[150px] focus:outline-none text-gray-800`}
            placeholder={isElementary ? 'Type your question here!' : 'Ask me any question about your studies...'}
            value={message}
            onChange={(e) => {
              if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                setMessage(e.target.value);
              }
            }}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={disabled || isSubmitting}
          ></textarea>

          {isStreaming ? (
            <button
              type="button"
              onClick={onStopStreaming}
              className={`send-btn absolute right-1 sm:right-2 bottom-1 sm:bottom-2 rounded-full ${isElementary ? 'p-2 sm:p-3' : 'p-1.5 sm:p-2'} transition-all bg-gray-800 text-white hover:bg-gray-900`}
              aria-label="Stop generating"
            >
              <Stop size={isElementary ? 18 : 14} weight="fill" />
            </button>
          ) : (
            <button
              type="submit"
              className={`send-btn absolute right-1 sm:right-2 bottom-1 sm:bottom-2 rounded-full ${isElementary ? 'p-2 sm:p-3' : 'p-1.5 sm:p-2'} transition-all ${
                !canSend
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-800 text-white hover:bg-black'
              }`}
              disabled={!canSend}
              aria-label="Send message"
            >
              <ArrowUp size={isElementary ? 20 : 16} weight="bold" />
            </button>
          )}
        </div>
      </form>

      {/* Character count indicator */}
      {showCharCount && (
        <p className={`text-xs mt-1 text-right ${charsRemaining < 200 ? 'text-red-500' : 'text-gray-400'}`}>
          {charsRemaining} characters remaining
        </p>
      )}
    </div>
  );
};

export default MessageInput;
